/**
 * Live ticker strip — scrolling marquee of indices + majors + Simon's
 * top holdings. Polls /api/terminal/ticker-strip every 30s. Clicking
 * a symbol pre-fills the terminal with `/set SYMBOL` for context.
 */
import { useEffect, useState } from 'react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

interface TickerItem {
  symbol: string
  price: number
  change: number | null
  change_pct: number | null
}

interface Props {
  onSymbolClick?: (symbol: string) => void
}

const POLL_MS = 30_000

export function TickerStrip({ onSymbolClick }: Props) {
  const [items, setItems] = useState<TickerItem[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function poll() {
      try {
        const r = await api.get<{ items: TickerItem[] }>('/api/terminal/ticker-strip')
        if (!cancelled) setItems(r.data.items ?? [])
      } catch {
        // swallow — strip is non-essential UI
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }
    poll()
    const id = setInterval(poll, POLL_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  if (!loaded || items.length === 0) {
    return (
      <div className="h-7 overflow-hidden border-b border-crt-green/20 bg-black/40" aria-hidden />
    )
  }

  return (
    <div
      className="relative h-7 overflow-hidden border-b border-crt-green/20 bg-black/40"
      role="marquee"
      aria-label="Live ticker strip"
    >
      <div className="ticker-strip-track flex items-center gap-6 whitespace-nowrap px-4 h-full animate-ticker">
        {/* Render twice so the marquee loops seamlessly */}
        {[...items, ...items].map((it, i) => {
          const pos = (it.change_pct ?? 0) > 0
          const neg = (it.change_pct ?? 0) < 0
          const tone = pos ? 'text-crt-green' : neg ? 'text-red-400' : 'text-crt-dim'
          return (
            <button
              key={`${it.symbol}-${i}`}
              type="button"
              onClick={() => onSymbolClick?.(it.symbol)}
              className="font-mono text-xs flex items-center gap-1.5 hover:bg-crt-green/10 px-1 py-0.5 rounded transition-colors"
              title={`Click to /set ${it.symbol}`}
            >
              <span className="text-crt-bright font-bold">{it.symbol}</span>
              <span className="text-crt-green/80">
                {it.price < 1000 ? it.price.toFixed(2) : Math.round(it.price).toLocaleString()}
              </span>
              <span className={clsx('shrink-0', tone)}>
                {it.change_pct !== null ? (
                  <>{pos ? '▲' : neg ? '▼' : '·'} {Math.abs(it.change_pct).toFixed(2)}%</>
                ) : (
                  <span className="text-crt-dim">—</span>
                )}
              </span>
            </button>
          )
        })}
      </div>

      {/* Fade edges for visual polish */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-black/60 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-black/60 to-transparent" />

      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          animation: ticker-scroll 60s linear infinite;
        }
        .animate-ticker:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  )
}
